/**
 * Asset Form Dialog Component
 * Dialog wrapper for creating and editing assets
 */

import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useNavigate } from 'react-router-dom';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '../ui/sheet';
import { AssetForm } from './AssetForm';
import { useCreateAsset, useUpdateAsset } from '../../stores/asset-list';
import { createAssetSchema, updateAssetSchema } from '../../lib/validation/asset-schema';
import type { CreateAssetFormData, UpdateAssetFormData } from '../../lib/validation/asset-schema';
import type { Asset } from '../../types/api';
import { toast } from 'sonner';
import { useIsMobile } from '../../hooks/use-mobile';
import { useLiveForm } from '../../hooks/useLiveForm';
import { usePresenceStore, useRegisterPresence } from '../../stores/presence';
import { useConnectionStore } from '../../stores/connection';
import { PresenceAvatars } from '../ui/presence-avatars';

interface AssetFormDialogProps {
  mode: 'create' | 'edit';
  asset?: Asset;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess?: () => void;
}

export function AssetFormDialog({
  mode,
  asset,
  open,
  onOpenChange,
  onSuccess,
}: AssetFormDialogProps) {
  const navigate = useNavigate();
  const isMobile = useIsMobile();

  const form = useForm<CreateAssetFormData | UpdateAssetFormData>({
    resolver: zodResolver(mode === 'create' ? createAssetSchema : updateAssetSchema),
    defaultValues: asset
      ? {
          asset_nickname: asset.asset_nickname,
          category_id: asset.category_id,
          subcategory_id: asset.subcategory_id,
          street_address: asset.street_address,
          street_address_other: asset.street_address_other,
          specific_location: asset.specific_location,
          lifecycle_status: asset.lifecycle_status,
          disposition_status: asset.disposition_status,
          disposition_other: asset.disposition_other,
          disposal_avenue: asset.disposal_avenue,
          asking_price: asset.asking_price,
          lowest_acceptable_price: asset.lowest_acceptable_price,
          current_highest_offer: asset.current_highest_offer,
          final_sale_price: asset.final_sale_price,
          shipping_costs: asset.shipping_costs,
          delivery_method: asset.delivery_method,
          delivery_method_other: asset.delivery_method_other,
          marketing_description: asset.marketing_description,
          responsible_person_id: asset.responsible_person_id,
          share_with_external: asset.share_with_external,
        }
      : {
          lifecycle_status: 'Inventoried',
          share_with_external: false,
        },
  });

  const createMutation = useCreateAsset();
  const updateMutation = useUpdateAsset();

  const liveForm = useLiveForm({
    entity: "asset",
    entityId: asset?.asset_id,
    topic: `assets.item.${asset?.asset_id}`,
    enabled: mode === "edit" && open,
  });

  useRegisterPresence("asset", asset?.asset_id, mode === "edit" && open);

  usePresenceStore.live({ entity: "asset" });
  const presenceMap = usePresenceStore(s => s.presence);
  const instanceId = useConnectionStore(s => s.instanceId);
  const viewers = (mode === "edit" && asset?.asset_id && presenceMap?.[asset.asset_id]) || [];

  useEffect(() => {
    if (open && mode === "edit" && asset) {
      form.reset({
        asset_nickname: asset.asset_nickname,
        category_id: asset.category_id,
        subcategory_id: asset.subcategory_id,
        street_address: asset.street_address,
        street_address_other: asset.street_address_other,
        specific_location: asset.specific_location,
        lifecycle_status: asset.lifecycle_status,
        disposition_status: asset.disposition_status,
        disposition_other: asset.disposition_other,
        disposal_avenue: asset.disposal_avenue,
        asking_price: asset.asking_price,
        lowest_acceptable_price: asset.lowest_acceptable_price,
        current_highest_offer: asset.current_highest_offer,
        final_sale_price: asset.final_sale_price,
        shipping_costs: asset.shipping_costs,
        delivery_method: asset.delivery_method,
        delivery_method_other: asset.delivery_method_other,
        marketing_description: asset.marketing_description,
        responsible_person_id: asset.responsible_person_id,
        share_with_external: asset.share_with_external,
      });
    }
  }, [open, mode, asset, form]);

  useEffect(() => {
    if (mode !== "edit" || !open) return;
    const sd = liveForm.serverData as Record<string, unknown> | undefined;
    if (!sd || typeof sd !== "object") return;
    const fields = [
      "asset_nickname", "category_id", "subcategory_id", "street_address",
      "street_address_other", "specific_location", "lifecycle_status",
      "disposition_status", "disposition_other", "disposal_avenue",
      "asking_price", "lowest_acceptable_price", "current_highest_offer",
      "final_sale_price", "shipping_costs", "delivery_method",
      "delivery_method_other", "marketing_description", "share_with_external",
      "responsible_person_id",
    ] as const;
    for (const f of fields) {
      if (f in sd) form.setValue(f as any, sd[f] as any, { shouldDirty: false });
    }
  }, [liveForm.serverData, mode, open, form]);

  async function onSubmit(data: CreateAssetFormData | UpdateAssetFormData) {
    try {
      if (mode === 'create') {
        const newAsset = await createMutation.mutateAsync(data as CreateAssetFormData) as Asset;
        toast.success('Asset created successfully');
        handleClose();
        onSuccess?.();
        navigate(`/assets/${newAsset.asset_id}`);
      } else {
        if (liveForm.isDirty) {
          const result = await liveForm.submit() as { ok: boolean; applied: number; edits: Record<string, unknown> } | undefined;
          if (result?.edits && Object.keys(result.edits).length > 0) {
            await updateMutation.mutateAsync({ assetId: asset!.asset_id, updates: result.edits as UpdateAssetFormData });
          }
        } else {
          await updateMutation.mutateAsync({ assetId: asset!.asset_id, updates: data as UpdateAssetFormData });
        }
        toast.success('Asset updated successfully');
        handleClose();
        onSuccess?.();
      }
    } catch (error: any) {
      console.error('Form submission error:', error);
      if (error.message) {
        toast.error(error.message);
      } else {
        toast.error(`Failed to ${mode === 'create' ? 'create' : 'update'} asset`);
      }
    }
  }

  function handleClose() {
    if (mode === "edit") {
      liveForm.discard();
    }
    form.reset();
    onOpenChange(false);
  }

  const title = mode === 'create' ? 'Create Asset' : 'Edit Asset';
  const description =
    mode === 'create'
      ? 'Add a new asset to the inventory'
      : 'Update asset information';

  const content = (
    <AssetForm
      form={form}
      onSubmit={onSubmit}
      mode={mode}
      isPending={createMutation.isPending || updateMutation.isPending || liveForm.isSubmitting}
      liveField={mode === "edit" ? liveForm.field : undefined}
    />
  );

  if (isMobile) {
    return (
      <Sheet open={open} onOpenChange={handleClose}>
        <SheetContent side="bottom" className="h-[90vh] overflow-y-auto">
          <SheetHeader>
            <div className="flex items-center gap-2">
              <SheetTitle>{title}</SheetTitle>
              {viewers.length > 0 && (
                <PresenceAvatars viewers={viewers} currentInstanceId={instanceId} />
              )}
            </div>
            <SheetDescription>{description}</SheetDescription>
          </SheetHeader>
          <div className="mt-6">{content}</div>
        </SheetContent>
      </Sheet>
    );
  }

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[700px] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <div className="flex items-center gap-2">
            <DialogTitle>{title}</DialogTitle>
            {viewers.length > 0 && (
              <PresenceAvatars viewers={viewers} currentInstanceId={instanceId} />
            )}
          </div>
          <DialogDescription>{description}</DialogDescription>
        </DialogHeader>
        {content}
      </DialogContent>
    </Dialog>
  );
}
